import { effect } from '@angular/core';
import { Component, input, output } from '@angular/core';
import { Modal } from 'flowbite';
import moment from 'moment';
import { AnimationOptions } from 'ngx-lottie';
import { IRoutePreview } from 'src/app/models';
import { ExpeditionStatus, InputType, NotifyOptions, RefundOptions, RouteEditMode, UpdateExpeditionStatus } from 'src/app/models/enums';
import { UtilsService } from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { images } from 'src/images';

@Component({
  selector: 'app-modal-confirm-changes',
  templateUrl: './modal-confirm-changes.component.html',
  styleUrl: './modal-confirm-changes.component.scss',
  standalone: false
})
export class ModalConfirmChangesComponent {
  readonly id = input<string>();
  readonly isOpen = input<boolean>();
  readonly modal = input<Modal>();
  readonly mode = input<string>();
  readonly title = input<string>();
  readonly confirmStep = input<number>();
  readonly loading = input<Boolean>();
  readonly previewChanges = input<IRoutePreview[]>([]);
  readonly loadingPercentage = input<number | null>();
  readonly loadingTitle = input<string>('services.routes.design.update.loading.title');
  readonly scheduling = input<boolean>(false);

  action = output<any>();
  cancelAction = output<any>();

  public images = images;
  public RouteEditMode = RouteEditMode;
  public loadingAnimation: AnimationOptions = {
    path: '/assets/animations/loading.json'
  };

  showPreview: boolean = false;
  scheduleDate: string | null = null;
  scheduleTime: string | null = null;
  scheduleDateElement: any;
  scheduleTimeElement: any;
  includeDiscardedExpeditions: boolean = false;
  excludedTripsIds: number[] = [];
  selectedPreviewRoute: IRoutePreview | null = null;

  get hasPercentage(): boolean {
    return this.loadingPercentage() != null;
  }

  get showRefundOptions(): boolean {
    return !!this.selectedPreviewRoute?.ticketing;
  }

  get showRelatedOptions(): boolean {
    return !!this.selectedPreviewRoute?.roundTrip;
  }

  selectedExpeditionStatus = UpdateExpeditionStatus.OPEN;
  expeditionStatusesElement: any;

  notificationOption: string = NotifyOptions.ALL;
  notificationOptions = Object.values(NotifyOptions);

  refundOption: string = RefundOptions.FULL_REFUND;
  refundOptions = Object.values(RefundOptions);

  deleteRelated: boolean = true;

  constructor(
    public literalService: LiteralService,
    public utilsService: UtilsService
  ) {
    effect(() => {
      if (this.isOpen()) {
        this.initExpeditionStatus();
        this.initScheduleInputs();
      }
    });
  }

  initScheduleInputs() {
    this.scheduleDate = null;
    this.scheduleTime = null;
    this.scheduleDateElement = {
      value: undefined,
      calendar: { showOnTop: false, hideCalendar: false, showYear: false, onlyFutureDays: true, possibleMultipleDates: false },
      edit: {
        required: true,
        inputType: InputType.DATE,
        valueToChange: 'scheduleDate',
        id: 'schedule-date',
        placeholder: this.literalService.get('services.routes.actions.scheduleUpdate.dateLabel', true),
      }
    };
    this.scheduleTimeElement = {
      value: undefined,
      edit: {
        required: true,
        inputType: InputType.TIME,
        valueToChange: 'scheduleTime',
        placeholder: this.literalService.get('services.routes.actions.scheduleUpdate.timeLabel', true),
      }
    };
  }

  private inputValue(event: any): string {
    return event?.target?.value ?? event?.value ?? event ?? '';
  }

  onScheduleDateChange(event: any) {
    this.scheduleDate = this.inputValue(event);
  }

  onScheduleTimeChange(event: any) {
    this.scheduleTime = this.inputValue(event);
  }

  private get scheduleMoment() {
    return moment(`${this.scheduleDate} ${this.scheduleTime}`, 'DD/MM/YYYY HH:mm', true);
  }

  get scheduleComplete(): boolean {
    return !!this.scheduleDate && !!this.scheduleTime && this.scheduleMoment.isValid() && this.scheduleMoment.isAfter(moment());
  }

  onConfirmSchedule() {
    if (!this.scheduleComplete) return;
    this.action.emit({ dateTime: this.scheduleMoment.toDate() });
  }

  initExpeditionStatus() {
    this.expeditionStatusesElement = {
      value: this.selectedExpeditionStatus,
      edit: {
        inputType: InputType.SELECT_SEARCH,
        values: Object.values(UpdateExpeditionStatus).map(status => ({
          id: status,
          value: status,
          label: this.literalService.get(`services.routes.design.update.expeditions.statuses.${status.toLowerCase()}`, true)
        })),
        valueToChange: 'expeditionStatuses'
      }
    };
  }

  close() {
    this.modal()!.hide();
  }

  onConfirmUpdate() {
    let rebuildStatus = [ExpeditionStatus.OPEN];
    switch(this.selectedExpeditionStatus) {
      case UpdateExpeditionStatus.OPEN_AND_READY:
        rebuildStatus.push(ExpeditionStatus.READY);
        break;
      case UpdateExpeditionStatus.ALL:
        rebuildStatus.push(ExpeditionStatus.READY);
        rebuildStatus.push(ExpeditionStatus.ONGOING);
    }
    if (this.mode() === RouteEditMode.ALTERATIONS) this.showPreview = true;
    if (this.confirmStep() === 1) {
      this.action.emit({showPreview: this.showPreview, rebuildStatus});
    } else {
      const updateOptions = {
        rebuild: this.showPreview,
        rebuildStatus,
        sendNotification: this.notificationOption !== NotifyOptions.NO_NOTIFY,
        refund: this.refundOption !== RefundOptions.NO_REFUND,
        fullRefund: this.refundOption === RefundOptions.FULL_REFUND,
        cancelRelated: this.deleteRelated,
        skipTripsIds: this.excludedTripsIds
      };
      this.action.emit(updateOptions);
    }
  }

  onIncludeDiscardedExpeditionsChange(event: any) {
    this.includeDiscardedExpeditions = event.target.checked;
  }

  onExpeditionStatusChange(event: any) {
    this.selectedExpeditionStatus = event.value;
  }

  onPreviewRouteSelect(route: IRoutePreview | null) {
    this.selectedPreviewRoute = route;
  }

  onExcludeTrip(event: any) {
    this.excludedTripsIds = event;
  }
}
